/**
 * Lokaler UI-Zustand des Nachrichtenbereichs (je Kanal): Antwort-Ziel, bearbeitete Nachricht, Entwürfe,
 * Slowmode-Abklingzeit, Upload-Fortschritt, Sprung-Anfragen. Getrennt vom normalisierten App-Store.
 */
import { create } from 'zustand';
import type { DraftFile } from './send';

export interface UploadProgress {
  loaded: number;
  total: number;
  abort?: () => void;
}

interface MessagesUiState {
  replyTo: Record<string, string | null>;
  editing: Record<string, string | null>;
  /** channelId → Zeitpunkt (ms), ab dem wieder gesendet werden darf. */
  cooldownUntil: Record<string, number>;
  /** nonce → Upload-Fortschritt einer ausstehenden Nachricht. */
  uploads: Record<string, UploadProgress>;
  /** Sprung innerhalb eines geöffneten Kanals (z. B. Klick auf Antwort-Vorschau). */
  jump: { channelId: string; messageId: string; seq: number } | null;
  /** Ausgewählte, noch nicht gesendete Dateien je Kanal. */
  files: Record<string, DraftFile[]>;
  setFiles(channelId: string, files: DraftFile[]): void;
  setReply(channelId: string, messageId: string | null): void;
  setEditing(channelId: string, messageId: string | null): void;
  setCooldown(channelId: string, untilMs: number): void;
  setUpload(nonce: string, p: UploadProgress | null): void;
  requestJump(channelId: string, messageId: string): void;
  /** Sprung-Anfrage als erledigt markieren (nur, wenn inzwischen keine neuere vorliegt). */
  clearJump(seq: number): void;
}

let jumpSeq = 0;

export const useMessagesUi = create<MessagesUiState>((set) => ({
  replyTo: {},
  editing: {},
  cooldownUntil: {},
  uploads: {},
  jump: null,
  files: {},
  setFiles: (channelId, files) => set((s) => ({ files: { ...s.files, [channelId]: files } })),
  setReply: (channelId, messageId) =>
    set((s) => ({ replyTo: { ...s.replyTo, [channelId]: messageId }, editing: messageId ? { ...s.editing, [channelId]: null } : s.editing })),
  setEditing: (channelId, messageId) => set((s) => ({ editing: { ...s.editing, [channelId]: messageId } })),
  setCooldown: (channelId, untilMs) => set((s) => ({ cooldownUntil: { ...s.cooldownUntil, [channelId]: untilMs } })),
  setUpload: (nonce, p) =>
    set((s) => {
      const uploads = { ...s.uploads };
      if (p) uploads[nonce] = p;
      else delete uploads[nonce];
      return { uploads };
    }),
  requestJump: (channelId, messageId) => set({ jump: { channelId, messageId, seq: ++jumpSeq } }),
  clearJump: (seq) => set((s) => (s.jump?.seq === seq ? { jump: null } : s)),
}));

/** Entwürfe je Kanal (überleben Kanalwechsel, nicht aber ein Neuladen). */
export const drafts = new Map<string, string>();

/** Zeitpunkt des letzten gesendeten Tipp-Ereignisses je Kanal (Drosselung). */
export const typingSentAt = new Map<string, number>();

/** Eingabefelder je Kanal, damit „Antworten“ o. Ä. den Fokus setzen kann. */
const composerInputs = new Map<string, HTMLTextAreaElement>();

export function registerComposerInput(channelId: string, el: HTMLTextAreaElement | null): void {
  if (el) composerInputs.set(channelId, el);
  else composerInputs.delete(channelId);
}

export function focusComposer(channelId: string): void {
  const el = composerInputs.get(channelId);
  if (el && !el.disabled) el.focus();
}
